/**
 * Groups and runs for the OPEN competition CARDS in the Verseny tab.
 *
 * The card list deliberately holds no `groups` / `sessions` listener — those
 * are scoped to `activeCompId`, i.e. the one competition the user drilled
 * into. So a card could show neither what categories exist nor when the runs
 * are, which is exactly what someone deciding whether to enter wants to know
 * before they open anything.
 *
 * The competition doc does carry denormalized `groupNames` / `sessionNames`,
 * but names only — no colour, no age range, and crucially no capacity. So the
 * subcollections are fetched here instead. Two `getDocs` per competition,
 * against a handful of documents each.
 *
 * THREE RULES, because this is where the card list's cost lives (D8):
 *
 *   1. A collapsed card reads nothing. `compIds` is the OPEN set, not every
 *      active competition — the caller narrows it, `cardReadScope.test.js`
 *      checks that it does.
 *   2. Once fetched, a competition's details stay — for the gym session, in a
 *      store owned by `useCompTabSources` (docs/spec/01f-tab-loading.md), so a
 *      tab switch loses nothing. Closing and reopening a card costs no read;
 *      without this a user could be billed without limit by toggling. Each
 *      competition's details carry a read time: older than FRESH_MS, a
 *      (re-)open or a returning tab re-reads them in the background while the
 *      kept details stay on the card.
 *   3. The `visibilitychange` refresh re-reads the OPEN set only — also when
 *      the details are fresh (the user may have been away for an hour with
 *      the tab open).
 *
 * Occupancy is counted only for competitions whose registration is actually
 * OPEN: "which run is filling up" is a question you can act on, and a draft or
 * a closed competition would be paying for a statistic nobody can use.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchCompParts } from "../utils/competitionFetch";
import { countCompetitorsPerSession } from "../utils/sessionOccupancy";

/** How long a competition's details count as fresh. */
export const CARD_DETAILS_FRESH_MS = 60 * 1000;

const EMPTY = Object.freeze({});

/**
 * The store `useCompCardDetails` reads and writes. It lives in
 * `useCompTabSources` (app level), not in the tab, so the details outlive the
 * tab's unmount.
 *
 * The gym is part of the slot, and the refs follow it during render: a render
 * for another gym never shows the previous gym's details, and a read still in
 * flight for the old gym is discarded when it lands (its generation no longer
 * matches) — no reset effect that could run after the tab's first read.
 */
export function useCompCardDetailsStore(gymId) {
  const [slot, setSlot] = useState(() => ({ gym: gymId, byComp: EMPTY }));
  // What has already been fetched under the CURRENT generation, and when.
  const loadedRef = useRef({ gen: null, ids: new Map() });
  // compId -> generation of the read in flight, so a tab that comes back while
  // a read is running does not start a second one for the same competition.
  const inFlightRef = useRef(new Map());
  // Bumped when the full-page editor closes (the edits the name maps cannot
  // show). Kept here, not in the tab: a tab-local counter restarts at 0 on
  // every mount and would change the generation — and empty the kept details —
  // on the first return after an edit.
  const [editorRev, setEditorRev] = useState(0);

  const gymRef = useRef(gymId);
  if (gymRef.current !== gymId) {
    gymRef.current = gymId;
    loadedRef.current = { gen: null, ids: new Map() };
    inFlightRef.current = new Map();
  }

  const setByComp = useCallback((next) => {
    setSlot((s) => {
      const base = s.gym === gymId ? s.byComp : EMPTY;
      return { gym: gymId, byComp: typeof next === "function" ? next(base) : next };
    });
  }, [gymId]);
  const bumpEditorRev = useCallback(() => setEditorRev((r) => r + 1), []);

  const byComp = slot.gym === gymId ? slot.byComp : EMPTY;
  return useMemo(
    () => ({ byComp, setByComp, loadedRef, inFlightRef, editorRev, bumpEditorRev }),
    [byComp, setByComp, editorRev, bumpEditorRev],
  );
}

/**
 * @param {string} gymId
 * @param {string[]} compIds - the competitions whose card is OPEN
 * @param {string[]} openCompIds - the subset whose registration is open
 * @param {string} [ownRegKey] - changes whenever THIS user's own registrations
 *   or proxy guests change. A count is a snapshot, so entering a guest from
 *   the very card that shows the numbers would otherwise leave them stale
 *   until the tab lost and regained focus — the one stale reading the user is
 *   guaranteed to notice, because they caused it. Other people's entries still
 *   wait for the next refresh; that is the aggregate's trade-off.
 * @param {string} [revKey] - changes whenever the group/session STRUCTURE
 *   changes. Same reasoning as `ownRegKey`, for the other thing a card shows.
 *   Adding a group in the editor and coming straight back to the card left the
 *   new group missing until a pull-to-refresh: the fetch is one-shot, and an
 *   in-app navigation fires no `visibilitychange`, so nothing here had any way
 *   to know an edit had happened.
 * @param {object} store - from `useCompCardDetailsStore` (via `useCompTabSources`)
 * @returns {Record<string, {groups: object[], sessions: object[]}>}
 *   `sessions[].taken` is the competitor count, present only for capped runs
 *   of an open competition.
 */
export function useCompCardDetails(gymId, compIds, openCompIds, ownRegKey = "", revKey = "", store) {
  const { setByComp, loadedRef, inFlightRef } = store;
  // Join into primitives so a fresh array identity on every parent render
  // doesn't re-fire the fetch.
  const key = (compIds || []).slice().sort().join(",");
  const openKey = (openCompIds || []).slice().sort().join(",");

  useEffect(() => {
    if (!gymId) {
      setByComp({});
      loadedRef.current = { gen: null, ids: new Map() };
      inFlightRef.current = new Map();
      return;
    }
    // A generation is (gym, own-entry fingerprint, structure fingerprint): when
    // any of those moves, everything is stale and the set is emptied; when only
    // the open set grows, the ids already in it are served from the store
    // (rule 2).
    const gen = `${gymId}|${ownRegKey}|${revKey}`;
    if (loadedRef.current.gen !== gen) {
      loadedRef.current = { gen, ids: new Map() };
      inFlightRef.current = new Map();
      setByComp({});
    }
    if (!key) return;
    const open = new Set(openKey ? openKey.split(",") : []);

    const load = async (ids) => {
      // A read already running for this generation will land fresh data.
      const todo = ids.filter(id => inFlightRef.current.get(id) !== gen);
      if (!todo.length) return;
      todo.forEach(id => inFlightRef.current.set(id, gen));
      const results = await Promise.all(todo.map(async (compId) => {
        try {
          const { groups, sessions } =
            await fetchCompParts(gymId, compId, ["groups", "sessions"], { sorted: true });

          if (!open.has(compId)) return [compId, { groups, sessions }];
          const capped = sessions.filter(s => s.maxParticipants > 0).map(s => s.id);
          const counts = await countCompetitorsPerSession(gymId, compId, capped);
          return [compId, {
            groups,
            sessions: sessions.map(s => (s.id in counts ? { ...s, taken: counts[s.id] } : s)),
          }];
        } catch { return [compId, null]; }
      }));
      // Another generation or gym took over while reading: these results
      // describe a state that no longer exists. (No per-effect cancel flag: a
      // read must be able to land after the tab that started it is gone.)
      if (loadedRef.current.gen !== gen) return;
      const readAt = Date.now();
      // A failed re-read keeps the details already on the card; only a first
      // read that failed shows an empty card (and is retried when stale).
      const entries = results
        .filter(([id, parts]) => parts || !loadedRef.current.ids.has(id))
        .map(([id, parts]) => [id, parts || { groups: [], sessions: [] }]);
      for (const [id] of results) {
        loadedRef.current.ids.set(id, readAt);
        inFlightRef.current.delete(id);
      }
      setByComp(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    };

    const all = key.split(",");
    // What the open set gained since the last run (rule 2), plus what was
    // read longer than FRESH_MS ago — those re-read in the background while
    // the kept details stay on screen.
    const now = Date.now();
    const missing = all.filter(id => !loadedRef.current.ids.has(id));
    const stale = all.filter(id => loadedRef.current.ids.has(id)
      && now - loadedRef.current.ids.get(id) >= CARD_DETAILS_FRESH_MS);
    load([...missing, ...stale]);
    // No interval: the card list is browsed, not stared at. Coming back to the
    // tab is the moment a stale number would be noticed — and it re-reads the
    // open set only (rule 3).
    const onVisible = () => { if (document.visibilityState === "visible") load(all); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
    // The store's refs are stable; `setByComp` changes only with the gym.
  }, [gymId, key, openKey, ownRegKey, revKey, setByComp]); // eslint-disable-line

  return store.byComp;
}
